import React, { useEffect, useMemo, useState } from "react";
import { SimpleTreeView as TreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import * as S from "./styles";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import { Button, Tooltip } from "@mui/material";
import { KOGAreaButtons } from "@/main/components/shared/styles";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { getTranslateByKey, isNullUndefinedEmpty } from "@/main/services/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { isEmpty, isNil } from "lodash";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import { useForm } from "react-hook-form";
import {
	SearchHierarchyLevelsForm,
	flattenHierarchy,
	filterTreeView,
	emptyTreeItem,
	handleGetHierarchyTree,
	handleDeleteHierarchyItem,
	handleSaveHierarchyItem,
	removeNode,
	handleEditHierarchyItem,
	handleUpdateLevel,
} from "./hierarchyLevelsUtils";
import KOGAutoCompletePaginationControlled from "@/main/components/shared/inputAutoCompleteWithPagination/autoCompletePaginationControlled";
import { SearchTermRequest } from "@/main/models/common/searchTermRequest";
import { userHierarchyApi } from "@/main/api";
import { AvailableUsersForHierarchyRequest } from "@/main/models/request";
import { AvailableUsersHierarchyResponse, UserHierarchyTreeResponse } from "@/main/models/response";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import useAsyncModalConfirmReason from "@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";
import { useStyles } from "./styles";
import { toast } from "react-toastify";
import { UpdateHierarchyLevelRequest } from "@/main/models/request/hierarchyLevel";
import { ButtonsContainer } from "@/main/components/shared/modal/style";
import BasicModal from "@/main/components/shared/modal/basicModal";
import { KeyValue } from "@/main/models/common";
import { SystemApplicationEnum } from "@/main/services/enum/SystemApplicationEnum";
import KogModalFadeComponent from "@/main/components/modal/ModalFadeComponent";
import ModalConfirm from "@/main/components/shared/modalConfirm/modalConfirm";

const INITIAL_OPEN_MODAL_DELETE_ITEM = {
	open: false,
	messages: [] as KeyValue[],
};

interface ITabHierarchyLevels {
	idSystemApplication: SystemApplicationEnum;
}
export function TabHierarchyLevels({ idSystemApplication }: ITabHierarchyLevels) {
	const navigate = useNavigate();
	const classes = useStyles({});

	const [searchParams] = useSearchParams();

	const guidInsurerParams = searchParams.get("insurerGuid");

	const { control, watch, setValue } = useForm<SearchHierarchyLevelsForm>({
		defaultValues: { filter: "", user: null, levelName: "" },
	});

	const watchFilter = watch("filter");
	const watchUser = watch("user");
	const watchLevelName = watch("levelName");

	const [treeviewData, setTreeviewData] = useState<UserHierarchyTreeResponse[]>([]);
	const [resultUsers, setResultUsers] = useState<UserHierarchyTreeResponse[]>([]);

	const [editingNode, setEditingNode] = useState<UserHierarchyTreeResponse | null>(null);
	const [editingLevelNode, setEditingLevelNode] = useState<UserHierarchyTreeResponse | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();
	const [nodeItemSelected, setNodeItemSelected] = useState<string | null>(null);
	const [openModalConfirmDelete, setOpenModalConfirmDelete] = useState<boolean>(false);
	const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
	const [openModalErrorDeleteItem, setOpenModalErrorDeleteItem] = useState(INITIAL_OPEN_MODAL_DELETE_ITEM);

	const allItems = useMemo(() => flattenHierarchy(resultUsers), [resultUsers, resultUsers.length]);

	useEffect(() => {
		setExpandedNodes(allItems);
	}, [allItems]);

	useEffect(() => {
		if (guidInsurerParams) {
			loadTreeView();
		}
	}, [guidInsurerParams]);

	useEffect(() => {
		if (watchFilter) {
			const filteredTreeView = filterTreeView(resultUsers, watchFilter);
			setTreeviewData(filteredTreeView);
		} else {
			setTreeviewData(resultUsers);
		}
	}, [watchFilter]);

	useEffect(() => {
		if (watchUser) {
			if (editingNode && editingNode.guid) {
				updateUserInHierarchy(watchUser);
			} else {
				saveUserInHierarchy(watchUser);
			}
			setValue("user", null);
		}
	}, [watchUser]);

	const hasEmptyTreeItem = useMemo(() => {
		const checkNode = (nodes: UserHierarchyTreeResponse[]): boolean => {
			for (const node of nodes) {
				if (isEmpty(node.guid)) return true;
				if (node.children && node.children.length > 0) {
					if (checkNode(node.children)) return true;
				}
			}
			return false;
		};

		return checkNode(treeviewData);
	}, [treeviewData, resultUsers]);

	async function saveUserInHierarchy(userData: AvailableUsersHierarchyResponse) {
		if (isNil(editingNode)) return;

		const guidInsurer = guidInsurerParams ?? "";
		const guidUser = userData.key.toString();
		const guidUserHierarchyParent = editingNode.guidUserHierarchyParent ?? "";

		let resultConfirm = null;
		let filesRequest = undefined;

		resultConfirm = await getConfirmationReason(
			getTranslateByKey("information.confirmChangeTitle"),
			ReasonTypeEnum.ChangeCompany,
		);

		if (!resultConfirm.confirmed) return;

		if (resultConfirm.files && resultConfirm.files.length > 0) {
			filesRequest = await Promise.all(
				resultConfirm.files.map(async (item) => {
					const base64DataUrl = await toBase64(item);
					return {
						fileName: item.name,
						contentType: item.type,
						fileBase64: base64DataUrl as string,
					};
				}),
			);
		}

		setLoading(true);
		try {
			const response = await handleSaveHierarchyItem({
				guidInsurer,
				guidUser,
				guidUserHierarchyParent,
				changeJustification: {
					guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
					reasonDescription: resultConfirm?.observation || null,
					filesUpload: filesRequest,
				},
				idSystemApplication,
			});

			toast.success(getTranslateByKey("information.recordAddedSuccessfully"));
			setEditingNode(null);
			if (response) {
				loadTreeView();
			}
		} catch {
		} finally {
			setLoading(false);
		}
	}

	async function updateUserInHierarchy(userData: AvailableUsersHierarchyResponse) {
		if (isNil(editingNode)) return;

		const guidInsurer = guidInsurerParams ?? "";
		const guidNewUser = userData.key.toString();
		const guidUserHierarchy = editingNode.guid ?? "";

		let resultConfirm = null;
		let filesRequest = undefined;

		resultConfirm = await getConfirmationReason(
			getTranslateByKey("information.confirmChangeTitle"),
			ReasonTypeEnum.ChangeCompany,
		);

		if (!resultConfirm.confirmed) return;

		if (resultConfirm.files && resultConfirm.files.length > 0) {
			filesRequest = await Promise.all(
				resultConfirm.files.map(async (item) => {
					const base64DataUrl = await toBase64(item);
					return {
						fileName: item.name,
						contentType: item.type,
						fileBase64: base64DataUrl as string,
					};
				}),
			);
		}

		setLoading(true);
		try {
			const response = await handleEditHierarchyItem({
				guidInsurer,
				guidNewUser,
				guidUserHierarchy,
				changeJustification: {
					guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
					reasonDescription: resultConfirm?.observation || null,
					filesUpload: filesRequest,
				},
			});

			toast.success(getTranslateByKey("information.recordEditedSuccessfully"));

			setEditingNode(null);
			if (response) {
				loadTreeView();
			}
		} catch {
		} finally {
			setLoading(false);
		}
	}

	async function loadTreeView() {
		const guidInsurer = guidInsurerParams ?? "";
		if (isEmpty(guidInsurer)) return;

		const emptyItem = {
			...emptyTreeItem,
			userName: getTranslateByKey("information.noUserAssigned"),
		};

		setLoading(true);

		try {
			const response = await handleGetHierarchyTree(guidInsurer, idSystemApplication);
			const value = response && response.length > 0 ? response : [emptyItem];

			setResultUsers(value);
			setTreeviewData(value);
		} catch {
		} finally {
			setLoading(false);
		}
	}

	const handleNewTreeViewItem = (nodeId: string) => {
		function addChild(nodes: UserHierarchyTreeResponse[]): UserHierarchyTreeResponse[] {
			return nodes.map((node) => {
				if (node.guid === nodeId) {
					const newChild: UserHierarchyTreeResponse = {
						...emptyTreeItem,
						userName: getTranslateByKey("information.noUserAssigned"),
						guidUserHierarchyParent: nodeId,
					};

					setEditingNode(newChild);
					return { ...node, children: [newChild, ...node.children] };
				} else if (node.children && node.children.length > 0) {
					return { ...node, children: addChild(node.children) };
				}
				return node;
			});
		}
		setTreeviewData((prev) => addChild(prev));
		setResultUsers((prev) => addChild(prev));
	};

	async function handleDeleteTreeViewItem() {
		let resultConfirm = null;
		let filesRequest = undefined;

		resultConfirm = await getConfirmationReason(
			getTranslateByKey("information.confirmChangeTitle"),
			ReasonTypeEnum.ChangeCompany,
		);

		if (!resultConfirm.confirmed) {
			return;
		}

		if (resultConfirm.files && resultConfirm.files.length > 0) {
			filesRequest = await Promise.all(
				resultConfirm.files.map(async (item) => {
					const base64DataUrl = await toBase64(item);
					return {
						fileName: item.name,
						contentType: item.type,
						fileBase64: base64DataUrl as string,
					};
				}),
			);
		}

		const guidInsurer = guidInsurerParams ?? "";
		const guidUserHierarchy = nodeItemSelected ?? "";

		setLoading(true);
		try {
			const resDeleteItem = await handleDeleteHierarchyItem({
				guidInsurer,
				guidUserHierarchy,
				changeJustification: {
					guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
					reasonDescription: resultConfirm?.observation || null,
					filesUpload: filesRequest,
				},
			});

			handleCloseModalDelete();

			if (!resDeleteItem.result && resDeleteItem.messages && resDeleteItem.messages.length > 0) {
				setOpenModalErrorDeleteItem({
					open: true,
					messages: resDeleteItem.messages,
				});
				return;
			}

			if (resDeleteItem.result) {
				toast.success(getTranslateByKey("information.recordRemovedSuccessfully"));
				loadTreeView();
			}
		} catch {
		} finally {
			setLoading(false);
		}
	}

	function handleOpenModalDelete(nodeId: string) {
		setNodeItemSelected(nodeId);
		setOpenModalConfirmDelete(true);
	}

	function handleCloseModalDelete() {
		setOpenModalConfirmDelete(false);
		setNodeItemSelected(null);
	}

	function handleConfirmModalDelete() {
		setOpenModalConfirmDelete(false);
		handleDeleteTreeViewItem();
	}

	function handleRemoveLocalStateTreeViewItem(node: UserHierarchyTreeResponse) {
		setTreeviewData((prev) => removeNode(prev, node));
		setEditingNode(null);
	}

	const handleEditTreeViewItem = (node: UserHierarchyTreeResponse) => {
		if (editingNode == null) {
			setEditingNode(node);
		} else {
			setEditingNode(null);
		}
	};

	const handleEditLevelItem = (node: UserHierarchyTreeResponse) => {
		if (editingLevelNode == null) {
			setEditingLevelNode(node);
			setValue("levelName", node.nameHierarchyLevel ?? "");
		} else {
			setEditingLevelNode(null);
			setValue("levelName", "");
		}
	};

	async function getUsersAPI({ searchTerm, ...rest }: SearchTermRequest) {
		const guidInsurer = guidInsurerParams ?? "";
		const request: AvailableUsersForHierarchyRequest = {
			...rest,
			guidInsurer,
			name: searchTerm ?? "",
			idSystemApplication,
		};
		const response = await userHierarchyApi.getAvailableUsersForHierarchy(request);
		return response;
	}

	function handleClickIconEditItem(
		event: React.MouseEvent<SVGSVGElement, MouseEvent>,
		item: UserHierarchyTreeResponse,
	) {
		event.stopPropagation();
		handleEditTreeViewItem(item);
	}

	function handleClickIconEditLevelItem(
		event: React.MouseEvent<SVGSVGElement, MouseEvent>,
		item: UserHierarchyTreeResponse,
	) {
		event.stopPropagation();
		handleEditLevelItem(item);
	}

	async function handleClickIconSaveLevelItem(
		event: React.MouseEvent<SVGSVGElement, MouseEvent>,
		item: UserHierarchyTreeResponse,
	) {
		event.stopPropagation();

		const guidInsurer = guidInsurerParams ?? "";

		const resultConfirm = await getConfirmationReason(
			getTranslateByKey("information.confirmChangeTitle"),
			ReasonTypeEnum.ChangeCompany,
		);

		if (!resultConfirm.confirmed) return;

		let filesRequest = undefined;
		if (resultConfirm.files && resultConfirm.files.length > 0) {
			filesRequest = await Promise.all(
				resultConfirm.files.map(async (item) => {
					const base64DataUrl = await toBase64(item);
					return {
						fileName: item.name,
						contentType: item.type,
						fileBase64: base64DataUrl as string,
					};
				}),
			);
		}

		const request: UpdateHierarchyLevelRequest = {
			guidInsurer,
			guid: item.guidHierarchyLevel,
			name: watchLevelName ? watchLevelName.trim() : undefined,
			changeJustification: {
				guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
				reasonDescription: resultConfirm?.observation || null,
				filesUpload: filesRequest,
			},
		};

		setLoading(true);
		try {
			await handleUpdateLevel(request);
			setEditingLevelNode(null);
			loadTreeView();
		} catch {
		} finally {
			setLoading(false);
		}
	}

	function renderLabel(item: UserHierarchyTreeResponse) {
		const enabledAddButton = !isEmpty(item.guid) && !loading && !editingNode && !hasEmptyTreeItem;

		return (
			<S.ContainerTreeView>
				<S.ContentTreeViewItem>
					{!isEmpty(item.guid) && (isNil(editingNode) || editingNode?.guid != item.guid) && (
						<>
							{editingLevelNode?.guid === item.guid ? (
								<>
									<SaveIcon
										onClick={(e) => handleClickIconSaveLevelItem(e, item)}
										fontSize="small"
										style={{ cursor: "pointer" }}
									/>
									<CancelIcon
										onClick={(e) => handleClickIconEditLevelItem(e, item)}
										fontSize="small"
										style={{ cursor: "pointer" }}
									/>
								</>
							) : (
								<EditIcon
									onClick={(e) => handleClickIconEditLevelItem(e, item)}
									fontSize="small"
									style={{ cursor: "pointer" }}
								/>
							)}

							<Tooltip title={item.nameHierarchyLevel} placement="top">
								{editingLevelNode?.guid === item.guid ? (
									<div
										role="presentation"
										style={{ width: "100%", maxWidth: 600, padding: "8px 0" }}
										onClick={(e) => e.stopPropagation()}
										onKeyDown={(e) => e.stopPropagation()}
									>
										<KogInputTextControlledNew
											name="levelName"
											id="id-searchHierarchyLevels-levelName"
											label={getTranslateByKey("information.level")}
											control={control}
											className={classes.inputText}
											inputProps={{ maxLength: 30 }}
										/>
									</div>
								) : (
									<span>
										{item.level ? `${getTranslateByKey("information.level")} ${item.level}` : ""}

										{item.nameHierarchyLevel ? ` - ${item.nameHierarchyLevel}` : ""}
									</span>
								)}
							</Tooltip>
						</>
					)}
				</S.ContentTreeViewItem>

				<S.ContentTreeViewItem>
					{editingNode?.guid === item.guid ? (
						<CancelIcon
							onClick={(e) => handleClickIconEditItem(e, item)}
							fontSize="small"
							style={{ cursor: "pointer" }}
						/>
					) : (
						<EditIcon
							onClick={(e) => handleClickIconEditItem(e, item)}
							fontSize="small"
							style={{ cursor: "pointer" }}
						/>
					)}

					<Tooltip title={item.email} placement="top">
						{editingNode?.guid === item.guid ? (
							<div
								role="presentation"
								style={{ minWidth: 300, maxWidth: 600, padding: "8px 0" }}
								onClick={(e) => e.stopPropagation()}
								onKeyDown={(e) => e.stopPropagation()}
							>
								<KOGAutoCompletePaginationControlled
									idScreen={`registerInsurer-autocomplete-hierarhy-levels`}
									name={`user`}
									loadingMessage={`${getTranslateByKey("information.searchingForUsers")}...`}
									control={control}
									getData={getUsersAPI}
									className={classes.inputAutocomplete}
									placeholder={getTranslateByKey("information.enterUser")}
								/>
							</div>
						) : (
							<span>
								{item.name ? item.name.concat(" - ") : ""}
								{item.userName} {item.userCompanyType ? `(${item.userCompanyType})` : ""}
							</span>
						)}
					</Tooltip>

					<AddIcon
						fontSize="small"
						style={{
							cursor: enabledAddButton ? "pointer" : "not-allowed",
						}}
						onClick={
							enabledAddButton
								? (e) => {
										e.stopPropagation();
										handleNewTreeViewItem(item.guid);
									}
								: undefined
						}
					/>
					<RemoveIcon
						fontSize="small"
						style={{
							cursor:
								isEmpty(item.guid) && isNullUndefinedEmpty(item.guidUserHierarchyParent) && treeviewData.length == 1
									? "not-allowed"
									: "pointer",
						}}
						onClick={(e) => {
							e.stopPropagation();

							if (
								isEmpty(item.guid) &&
								isNullUndefinedEmpty(item.guidUserHierarchyParent) &&
								treeviewData.length == 1
							) {
								return;
							}

							if (!isEmpty(item.guid)) {
								handleOpenModalDelete(item.guid);
							} else {
								handleRemoveLocalStateTreeViewItem(item);
							}
						}}
					/>
				</S.ContentTreeViewItem>
			</S.ContainerTreeView>
		);
	}

	function renderTreeItem(item: UserHierarchyTreeResponse) {
		return (
			<TreeItem
				key={item.guid}
				itemId={item.guid}
				label={renderLabel(item)}
				sx={{
					"& .MuiTreeItem-content.Mui-focused": {
						backgroundColor: "rgba(63, 94, 201, 0.08)",
					},
					"& .MuiTreeItem-root": {
						marginLeft: "10px",
					},
				}}
			>
				{item.children.map((child) => renderTreeItem(child))}
			</TreeItem>
		);
	}

	function handleBack() {
		navigate(RoutesConstants.registrationSearchInsurer);
	}

	return (
		<S.Container>
			<S.ContentTreeViewCard>
				<S.ContentTreeViewCardTitle>
					<div>
						<strong>{getTranslateByKey("information.hierarchy")}</strong>
					</div>

					<div style={{ maxWidth: "300px" }}>
						<KogInputTextControlledNew
							name="filter"
							id="id-searchHierarchyLevels-txt"
							label={getTranslateByKey("information.typeToFilter")}
							control={control}
							className={classes.inputText}
						/>
					</div>
				</S.ContentTreeViewCardTitle>

				{loading && <LoadingComponent />}

				<S.ContentTreeViewCardContent>
					<TreeView
						slots={{
							collapseIcon: ExpandMoreIcon,
							expandIcon: ChevronRightIcon,
						}}
						expandedItems={expandedNodes}
						onExpandedItemsChange={(_, nodeIds) => setExpandedNodes(nodeIds)}
					>
						{treeviewData.map(renderTreeItem)}
					</TreeView>
				</S.ContentTreeViewCardContent>
			</S.ContentTreeViewCard>

			<KOGAreaButtons $isRegisterScreen style={{ marginTop: "3rem" }}>
				<SecondaryButton type="button" onClick={handleBack} id="id-registerInsurer-btn-back" disabled={loading}>
					{getTranslateByKey("information.back")}
				</SecondaryButton>
			</KOGAreaButtons>

			<KogModalFadeComponent open={openModalConfirmDelete} setOpen={setOpenModalConfirmDelete}>
				<ModalConfirm
					titleModal={getTranslateByKey("information.hierarchyLevelsConfirmMessageDelete")}
					handleModalCancel={handleCloseModalDelete}
					handleModalConfirm={handleConfirmModalDelete}
				/>
			</KogModalFadeComponent>

			<BasicModal
				open={openModalErrorDeleteItem.open}
				boxStyle={{ width: "50%" }}
				handleClose={() => setOpenModalErrorDeleteItem(INITIAL_OPEN_MODAL_DELETE_ITEM)}
			>
				<div style={{ padding: 20 }}>
					{openModalErrorDeleteItem.messages.map((item, indexItem) => (
						<S.ContainerErrorMessages key={indexItem}>
							<h5>
								<strong>{item.key}</strong>
							</h5>
							<ul>
								{(JSON.parse(item.value) as string[]).map((itemValue, indexItemValue) => (
									<li key={indexItemValue}>{itemValue}</li>
								))}
							</ul>
						</S.ContainerErrorMessages>
					))}
					<ButtonsContainer style={{ marginTop: 35 }}>
						<Button
							id={"id-searchHierarchyLevels-modal-alert-ok-button"}
							variant="contained"
							onClick={() => setOpenModalErrorDeleteItem(INITIAL_OPEN_MODAL_DELETE_ITEM)}
						>
							{getTranslateByKey("information.ok")}
						</Button>
					</ButtonsContainer>
				</div>
			</BasicModal>
			<ModalConfirmationReason closeByOverlay={false} />
		</S.Container>
	);
}
